import React, { useState } from "react";
import { motion } from "framer-motion";
import {
  ShieldCheck,
  ArrowRight,
  CheckCircle2,
} from "lucide-react";

import heroImg from "../../../../Assets/Images/whatsapp/business-sms-hero.png";
import ContactModal from "../../../../Assets/Common/ContactModal";

// ============================================================
// SMS OTP SERVICE HERO SECTION
// ============================================================

const SmsOtpService = () => {
  const [openModal, setOpenModal] = useState(false);

  return (
    <section
      id="sms-otp-hero"
      className="
        relative
        overflow-hidden
        theme-bg-primary
        py-12
        font-saira
        lg:py-32
      "
    >
      {/* ======================================================
          BACKGROUND GLOW
      ====================================================== */}

      <div
        className="
          absolute
          left-1/2
          top-0
          h-[500px]
          w-[500px]
          -translate-x-1/2
          rounded-full
          bg-orange-500/10
          blur-[180px]
        "
      />

      {/* ======================================================
          MAIN CONTAINER
      ====================================================== */}

      <div className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="grid items-center gap-14 lg:grid-cols-2">

          {/* ==================================================
              LEFT CONTENT
          ================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              x: -60,
            }}
            whileInView={{
              opacity: 1,
              x: 0,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.7,
            }}
          >
            {/* ==================================================
                BADGE
            ================================================== */}

            <span
              className="
                inline-flex
                items-center
                gap-2
                rounded-full
                border
                border-orange-500/30
                bg-orange-500/10
                px-5
                py-2
                text-sm
                font-medium
              "
              style={{
                color: "var(--brand-light)",
              }}
            >
              <ShieldCheck size={18} />

              Secure SMS OTP Platform
            </span>

            {/* ==================================================
                HEADING
            ================================================== */}

            <h1
              className="
                mt-3
                text-4xl
                font-bold
                leading-tight
                lg:text-5xl
              "
              style={{
                color: "var(--text-primary)",
              }}
            >
              Secure
              <span
                style={{
                  color: "var(--brand-light)",
                }}
              >
                {" "}SMS OTP
              </span>
              <br />
              Verification for Modern Businesses
            </h1>

            {/* ==================================================
                DESCRIPTION
            ================================================== */}

            <p
              className="
                mt-8
                text-lg
                leading-8
              "
              style={{
                color: "var(--text-secondary)",
              }}
            >
              Deliver fast, secure, and reliable OTP verification
              messages with a scalable SMS platform built for modern
              businesses. Protect user accounts, verify transactions,
              and authenticate customers with confidence.
            </p>

            {/* ==================================================
                CTA
            ================================================== */}

            <div className="mt-10 flex flex-wrap gap-5">
              <button
                type="button"
                onClick={() => {
                  setOpenModal(true);
                }}
                className="
                  group
                  flex
                  items-center
                  gap-2
                  rounded-xl
                  bg-orange-500
                  px-7
                  py-4
                  font-semibold
                  text-black
                  transition-all
                  duration-300
                  hover:bg-orange-400
                  hover:shadow-[0_12px_35px_rgba(249,115,22,0.22)]
                  active:scale-[0.98]
                "
                style={{
                  backgroundColor: "var(--brand-primary)",
                }}
              >
                Get Started

                <ArrowRight
                  size={18}
                  className="
                    transition
                    duration-300
                    group-hover:translate-x-1
                  "
                />
              </button>
            </div>

            {/* ==================================================
                TRUST INDICATORS
            ================================================== */}

            <div
              className="
                mt-12
                flex
                flex-wrap
                gap-8
                text-sm
              "
              style={{
                color: "var(--text-secondary)",
              }}
            >
              {/* Secure Verification */}

              <div className="flex items-center gap-2">
                <CheckCircle2
                  size={18}
                  style={{
                    color: "var(--brand-light)",
                  }}
                />

                Secure Verification
              </div>

              {/* Fast OTP Delivery */}

              <div className="flex items-center gap-2">
                <CheckCircle2
                  size={18}
                  style={{
                    color: "var(--brand-light)",
                  }}
                />

                Fast OTP Delivery
              </div>

              {/* API Integration */}

              <div className="flex items-center gap-2">
                <CheckCircle2
                  size={18}
                  style={{
                    color: "var(--brand-light)",
                  }}
                />

                Instant API Integration
              </div>
            </div>
          </motion.div>

          {/* ==================================================
              RIGHT IMAGE
          ================================================== */}

          <motion.div
            initial={{
              opacity: 0,
              scale: 0.9,
            }}
            whileInView={{
              opacity: 1,
              scale: 1,
            }}
            viewport={{
              once: true,
            }}
            transition={{
              duration: 0.8,
            }}
            className="relative"
          >
            {/* ==================================================
                HERO IMAGE
            ================================================== */}

            <motion.img
              src={heroImg}
              alt="SMS OTP verification platform"
              animate={{
                y: [0, -10, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
                ease: "easeInOut",
              }}
              className="
                h-[500px]
                w-full
                rounded-3xl
                border
                object-cover
                shadow-[0_0_70px_rgba(249,115,22,0.2)]
              "
              style={{
                borderColor: "var(--border-primary)",
              }}
            />

            {/* ==================================================
                TOP RIGHT CARD
            ================================================== */}

            <motion.div
              animate={{
                y: [0, -8, 0],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
              }}
              className="
                absolute
                right-0
                top-1
                rounded-2xl
                border
                border-orange-500/20
                px-3
                py-2
                backdrop-blur-xl
              "
              style={{
                backgroundColor: "color-mix(in srgb, var(--card-bg) 95%, transparent)",
                borderColor: "rgba(249, 115, 22, 0.2)",
              }}
            >
              <h3
                className="
                  mt-1
                  text-2xl
                  font-bold
                "
                style={{
                  color: "var(--brand-light)",
                }}
              >
                99.9%
              </h3>

              <p
                className="text-sm"
                style={{
                  color: "var(--text-secondary)",
                }}
              >
                OTP Delivery Success
              </p>
            </motion.div>

            {/* ==================================================
                BOTTOM LEFT CARD
            ================================================== */}

            <motion.div
              animate={{
                y: [0, 8, 0],
              }}
              transition={{
                duration: 5,
                repeat: Infinity,
              }}
              className="
                absolute
                bottom-5
                -left-4
                rounded-xl
                border
                px-5
                py-2
                backdrop-blur-xl
              "
              style={{
                backgroundColor: "color-mix(in srgb, var(--card-bg) 95%, transparent)",
                borderColor: "var(--border-primary)",
              }}
            >
              <h3
                className="
                  mt-1
                  text-2xl
                  font-bold
                "
                style={{
                  color: "var(--brand-light)",
                }}
              >
                500+
              </h3>

              <p
                className="text-sm"
                style={{
                  color: "var(--text-secondary)",
                }}
              >
                Businesses Secured
              </p>
            </motion.div>
          </motion.div>
        </div>
      </div>

      <ContactModal
        isOpen={openModal}
        onClose={() => setOpenModal(false)}
        initialService="API Services"
        initialSubService="SMS OTP Services"
      />
    </section>
  );
};

export default SmsOtpService;